/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import styled from '@emotion/styled';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Button } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { selectDilDefinitionByName } from 'srs-middleware';
import { filterProperties, getAllProperties } from 'utilities/srsUtils';
import { FacetSearch } from './facetSearch';
import { useFacetsState } from 'hooks/useFacetsState';
import { useFilters } from 'hooks/useFilters';
import { Sizes } from 'styles/variables-slykit';
import { alignedMinimalButton, DividerFull, FlexSplitter } from 'styles/styledComponentUtils';
import { FacetHeader } from 'components/facets/facetHeader';
import { ScrollBox } from 'components/scrollBox';

/**
 * MainPanel is responsible for rendering all of the available properties
 *
 *
 * @param {function} openPanel passed down from blueprint PanelStack component. Opens the Single Property Panel
 * @param {function} sortFacetsFunc used to apply a sort function to the facet list order using array sort
 * @param {array} filterPropertyProps pass array of params to useFilteredProperties, will filter which facets are in Property list
 */
export const MainPanel = ({ openPanel, sortFacetsFunc = null, filterPropertyProps = null }) => {
	const [properties, setProperties] = useState([]);
	const [collapsedProperties, setCollapsedProperties] = useState([]);
	const { resetAllFacets } = useFacetsState();
	const {
		state: {
			explorer: { selectedDefinitionName },
			transient: { rootResult },
		},
	} = useContext(WorkspaceContext);
	const isAllPropertiesCollapsed = collapsedProperties.length > 0;

	// responsible for determining which component to render for each of the different properties
	// if you want to add a new component to this
	const { renderComponents } = useFilters(properties);

	// get the actual definition
	const selectedDefinition = useMemo(() => {
		return selectDilDefinitionByName(selectedDefinitionName);
	}, [selectedDefinitionName]);

	useEffect(() => {
		if (rootResult) {
			let fetchedProperties;
			if (filterPropertyProps) {
				fetchedProperties = filterProperties(selectedDefinitionName, ...filterPropertyProps);
			} else {
				fetchedProperties = getAllProperties(selectedDefinition);
			}
			// If user provided sort function, apply to facet order
			if (sortFacetsFunc) {
				fetchedProperties.sort(sortFacetsFunc);
			} else {
				// default sort alpha on name
				fetchedProperties.sort((a, b) => (a.name < b.name ? -1 : Number(a.name > b.name)));
			}
			setProperties(fetchedProperties);
		} else {
			setProperties([]);
		}
	}, [rootResult, selectedDefinition, selectedDefinitionName, sortFacetsFunc, filterPropertyProps]);

	// reset collapsed states when properties change (default is collapse all)
	useEffect(() => {
		setCollapsedProperties(properties?.map((p) => p.name) || []);
	}, [properties]);

	/**
	 * @param {string} propertyName
	 */
	const toggleCollapseProperty = (propertyName) => {
		setCollapsedProperties((collapseState) => {
			if (collapseState.includes(propertyName)) {
				return collapseState.slice(0).filter((x) => x !== propertyName);
			}
			return [...collapseState, propertyName];
		});
	};

	// support expand/collapse all
	const collapseOrExpandAllProperties = () => {
		// ? expand : collapse
		setCollapsedProperties(isAllPropertiesCollapsed ? [] : properties.map((property) => property.name));
	};

	// used to switch to Single Property Panel
	const openNewPanel = (property, component) => {
		openPanel({
			component: component,
			props: { property },
			title: property.name,
		});
	};

	return (
		<Wrapper>
			<section>
				<FacetHeader
					title={'Filters'}
					onResetClick={resetAllFacets}
					css={css`
						margin: ${Sizes.md} ${Sizes.lg} ${Sizes.sm};
					`}
				/>

				<DividerFull />

				<div css={optionsRow}>
					<Button
						cy-test="expand-collapse-all"
						onClick={collapseOrExpandAllProperties}
						icon={isAllPropertiesCollapsed ? IconNames.EXPAND_ALL : IconNames.COLLAPSE_ALL}
						text={isAllPropertiesCollapsed ? 'Expand All' : 'Collapse All'}
						css={alignedMinimalButton}
						minimal
						small
					/>
					<FlexSplitter />
					<FacetSearch />
				</div>

				<DividerFull />
			</section>

			<ScrollBox
				innerProps={{
					css: css`
						padding-bottom: ${Sizes.xlg};
					`,
				}}
			>
				{renderComponents.map((rc) => {
					const { property, component } = rc;
					const toggleFunction = () => toggleCollapseProperty(property.name);
					const isOpen = !collapsedProperties.includes(property.name);
					return component(openNewPanel, toggleFunction, isOpen, property.name);
				})}
			</ScrollBox>
		</Wrapper>
	);
};

const Wrapper = styled.div`
	width: 100%;
	height: 100%;
	overflow: hidden;
	display: flex;
	flex-direction: column;
`;

const optionsRow = css`
	display: flex;
	margin: ${Sizes.xxsm} ${Sizes.lg};
	align-items: center;
`;
